import { Linkable } from '@/components/blocks/Linkable';
import { moveWishListItemMenu } from '@/components/content/HdmWishlistDetail/styles/moveWishListItemMenu';
import { WishListsCreateCard } from '@/components/content/WishListsV2/parts/CreateCard';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useWishListDetailsV2 } from '@/data/Content/WishListDetailsV2';
import { useWishLists } from '@/data/Content/WishLists';
import { ContentProvider } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { ProductType } from '@/data/types/Product';
import {
	Box,
	Divider,
	IconButton,
	Menu,
	MenuItem,
	SwipeableDrawer,
	Typography,
	useTheme,
} from '@mui/material';
import { WishlistWishlistItem } from 'integration/generated/transactions/data-contracts';
import { useTrackAddToWishList } from 'presentation/events/hooks/useTrackAddToWishList';
import { Dispatch, MouseEvent, SetStateAction, useCallback, useState } from 'react';
import {
	MATERIAL_SYMBOL_ICONS,
	OpenSansFontWeights,
} from 'shared/custom/constants/commonConstants';
import { useGetCategories } from 'shared/custom/hooks/Categories';
import { findParentCategories } from 'shared/custom/utils/getCategoryByIdForGA';

interface MoveWishListItemMenuProps {
	setAnchorEl: Dispatch<SetStateAction<HTMLElement | null>>;
	productAction: ProductType;
	anchorEl: HTMLElement | null;
	isPLP?: boolean;
	bundleId?: string;
}

export const MoveWishListItemMenu: React.FC<MoveWishListItemMenuProps> = ({
	anchorEl,
	setAnchorEl,
	productAction,
	isPLP = false,
	bundleId,
}) => {
	const theme = useTheme();
	const routes = useLocalization('Routes');
	const localization = useLocalization('WishList');

	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

	const payload = useWishLists();
	const { wishLists, mutateWishLists, resetCreationData, onCreate } = payload;
	const { wishList, onDeleteFromWishList } = useWishListDetailsV2();
	const { addToWishList } = useProductDetails({
		partNumber: '',
	});
	const trackAddToWishList = useTrackAddToWishList();
	const { navigation } = useGetCategories();

	const open = Boolean(anchorEl);

	const moveItemToAnotherWishListMessage = useCallback(
		(toWishListUniqueId: string) => (
			<>
				{isPLP ? localization.itemAdded.t() : localization.itemMoved.t()}&nbsp;
				<Linkable
					sx={moveWishListItemMenu.viewListLink}
					href={{
						pathname: routes.WishListDetails.route.t(),
						query: { id: toWishListUniqueId },
					}}
				>
					<Typography variant="bodyBaseline" fontWeight={OpenSansFontWeights.semibold}>
						{localization.viewWishList.t()}
					</Typography>
				</Linkable>
			</>
		),
		[isPLP, localization, routes]
	);

	const handleMoveItemClick = useCallback(
		async (toWishList: WishlistWishlistItem) => {
			if (!isPLP && wishList?.uniqueID) {
				await onDeleteFromWishList(productAction)();
			}
			await addToWishList(
				productAction.partNumber,
				1,
				toWishList,
				moveItemToAnotherWishListMessage(toWishList.uniqueID ?? ''),
				<DynamicIcon
					color={theme.palette.primary.main}
					name={isPLP ? MATERIAL_SYMBOL_ICONS.favorite : MATERIAL_SYMBOL_ICONS.swapHoriz}
					variant={'filled'}
				/>,
				isPLP
			)();
			if (mutateWishLists) await mutateWishLists();
			trackAddToWishList({
				product: bundleId
					? { ...productAction, id: productAction.uniqueID ?? productAction.partNumber }
					: productAction,
				categories: findParentCategories(navigation, productAction.parentCatalogGroupID) ?? [],
				quantity: 1,
				bundleId,
			});
			setAnchorEl(null);
			setIsDrawerOpen(false);
		},
		[
			isPLP,
			wishList?.uniqueID,
			productAction,
			onDeleteFromWishList,
			addToWishList,
			moveItemToAnotherWishListMessage,
			mutateWishLists,
			setAnchorEl,
			trackAddToWishList,
			navigation,
			bundleId,
			theme.palette,
		]
	);

	const handleClose = () => {
		setAnchorEl(null);
		resetCreationData();
		setIsDrawerOpen(false);
	};

	const handleCreateWishList = useCallback(
		async (e: MouseEvent<HTMLButtonElement>) => {
			const createdWishListResponse = await onCreate(e);
			if (!createdWishListResponse.error) {
				const { wishListCreated } = createdWishListResponse;
				if (!!wishListCreated) {
					handleMoveItemClick(wishListCreated);
				}
			}
		},
		[onCreate, handleMoveItemClick]
	);

	const menuList = wishLists.filter((w) => w.uniqueID !== wishList?.uniqueID);

	return (
		<ContentProvider value={payload}>
			<Menu
				id="move-wish-list-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				sx={moveWishListItemMenu.menuContainer}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: isPLP ? 'right' : 'center',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: isPLP ? 'right' : 'center',
				}}
				MenuListProps={{
					'aria-labelledby': 'move-wish-list-menu',
					role: 'listbox',
				}}
			>
				{menuList.map((wishList) => (
					<MenuItem key={wishList.uniqueID} onClick={() => handleMoveItemClick(wishList)}>
						{wishList.description}
					</MenuItem>
				))}
				<MenuItem onClick={() => setIsDrawerOpen(true)}>
					<DynamicIcon
						name={MATERIAL_SYMBOL_ICONS.add}
						color={theme.palette.primary.main}
						size="20px"
					/>
					<Typography
						variant="bodyBaseline"
						color="secondary.main"
						sx={moveWishListItemMenu.createANewListItem}
					>
						{localization.createANewWishList.t()}
					</Typography>
				</MenuItem>
			</Menu>
			<SwipeableDrawer
				anchor="right"
				open={isDrawerOpen}
				onOpen={() => setIsDrawerOpen(true)}
				onClose={handleClose}
				sx={moveWishListItemMenu.mobileDrawer}
			>
				<Box sx={moveWishListItemMenu.title}>
					<IconButton
						onClick={handleClose}
						sx={moveWishListItemMenu.closeIcon}
						className="drawer-close-icon"
					>
						<DynamicIcon name={MATERIAL_SYMBOL_ICONS.cancel} variant="rounded" />
					</IconButton>
					<Typography ml="5px" variant="textSegment" color="grey.300" className="drawer-title">
						{localization.createWishListTitle.t()}
					</Typography>
				</Box>
				<Divider sx={{ mb: '10px' }} />
				<Box sx={{ mx: '10px' }}>
					<WishListsCreateCard
						containerSxProp={moveWishListItemMenu.createCardContainer}
						neededTitle={false}
						isFormHiddenProp={false}
						handleCreateWishListProp={handleCreateWishList}
						handleCloseCreateWishListProp={handleClose}
					/>
				</Box>
			</SwipeableDrawer>
		</ContentProvider>
	);
};
